import { type FormEvent, useState } from "react";
import { Bell, CheckCircle2, Loader2, Mail } from "lucide-react";
import { trpc } from "@/lib/trpc";
import type { CommunityPageContent } from "@shared/communityPage";

/**
 * Small conversion card for the Community right rail.
 *
 * Subscription consent is collected separately from membership. The neutral
 * success message intentionally does not reveal whether an address was already
 * subscribed; activation still requires the confirmation sent to that inbox.
 */
export default function CommunityStayConnectedCard({ content }: { content?: CommunityPageContent["sidebar"] } = {}) {
  if (!trpc.notifications?.subscribeCommunityNewsletter?.useMutation) return null;
  return <ConnectedCommunityStayConnectedCard content={content} />;
}

function ConnectedCommunityStayConnectedCard({ content }: { content?: CommunityPageContent["sidebar"] }) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [accepted, setAccepted] = useState(false);
  const subscribe = trpc.notifications.subscribeCommunityNewsletter.useMutation({
    onSuccess: () => {
      setAccepted(true);
      setEmail("");
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    subscribe.mutate({ email, website: website || undefined });
  }

  return (
    <section className="community-stay-connected" aria-labelledby="community-stay-connected-title">
      <div className="community-stay-connected__icon" aria-hidden="true">
        <Mail size={24} strokeWidth={1.7} />
      </div>
      <div className="community-stay-connected__copy">
        <h2 id="community-stay-connected-title">{content?.stayConnectedTitle ?? "Stay connected"}</h2>
        <p>{content?.stayConnectedDescription ?? "Get a monthly digest of Community news and upcoming events."}</p>
      </div>
      {accepted ? (
        <div className="community-stay-connected__success" role="status">
          <CheckCircle2 size={17} aria-hidden="true" />
          <span>Check your inbox to confirm.</span>
        </div>
      ) : (
        <form className="community-stay-connected__form" onSubmit={submit}>
          <label htmlFor="community-newsletter-email">Email address</label>
          <input
            id="community-newsletter-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            maxLength={320}
            required
            value={email}
            onChange={event => setEmail(event.target.value)}
            placeholder="you@example.com"
            aria-describedby="community-newsletter-note"
          />
          <label className="community-stay-connected__honeypot" aria-hidden="true">
            Website
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={event => setWebsite(event.target.value)}
            />
          </label>
          <button className="community-stay-connected__cta" type="submit" disabled={subscribe.isPending}>
            {subscribe.isPending ? (
              <Loader2 className="community-stay-connected__spinner" size={15} aria-hidden="true" />
            ) : (
              <Bell size={15} strokeWidth={1.9} aria-hidden="true" />
            )}
            {subscribe.isPending ? "Sending…" : content?.subscribeLabel ?? "Subscribe to Updates"}
          </button>
          <small id="community-newsletter-note">{content?.newsletterNote ?? "Confirm by email. Unsubscribe anytime."}</small>
          {subscribe.error && (
            <p className="community-stay-connected__error" role="alert">
              {subscribe.error.message}
            </p>
          )}
        </form>
      )}
      <style>{`
        .community-stay-connected{
          position:relative;
          display:grid;
          grid-template-columns:auto minmax(0,1fr);
          align-items:start;
          column-gap:.8rem;
          gap:.85rem;
          overflow:hidden;
          padding:1.15rem;
          border:1px solid rgba(220,60,145,.28);
          border-radius:1rem;
          background:
            radial-gradient(circle at 100% 100%,rgba(220,60,145,.16),transparent 38%),
            linear-gradient(145deg,rgba(29,18,40,.96),rgba(13,13,28,.98));
          box-shadow:inset 0 1px 0 rgba(255,255,255,.06),0 16px 32px rgba(0,0,0,.18);
        }
        .community-stay-connected::before{
          position:absolute;
          inset:0 0 auto;
          height:1px;
          content:"";
          background:linear-gradient(90deg,transparent,#DC3C91 28%,#DBA514 72%,transparent);
          opacity:.8;
        }
        .community-stay-connected__icon{
          display:grid;
          width:3.2rem;
          height:3.2rem;
          place-items:center;
          border:1px solid rgba(249,168,212,.34);
          border-radius:.85rem;
          color:#F9A8D4;
          background:linear-gradient(145deg,rgba(220,60,145,.28),rgba(84,25,77,.28));
          box-shadow:inset 0 0 18px rgba(220,60,145,.16),0 8px 20px rgba(0,0,0,.18);
          grid-row:1;
        }
        .community-stay-connected__copy h2{
          margin:0;
          color:#FFFFFF;
          font-family:'Space Grotesk',sans-serif;
          font-size:1rem;
          font-weight:750;
          letter-spacing:-.02em;
          text-transform:uppercase;
        }
        .community-stay-connected__copy p{
          max-width:17rem;
          margin:.42rem 0 0;
          color:#B8C1D1;
          font-family:'Inter',sans-serif;
          font-size:.78rem;
          line-height:1.5;
        }
        .community-stay-connected__form{
          display:grid;
          gap:.55rem;
          grid-column:1 / -1;
        }
        .community-stay-connected__form>label:first-child{
          color:#E2E8F0;
          font-family:'Inter',sans-serif;
          font-size:.7rem;
          font-weight:700;
        }
        .community-stay-connected__form>input{
          width:100%;
          min-width:0;
          min-height:2.55rem;
          padding:.6rem .72rem;
          border:1px solid rgba(255,255,255,.15);
          border-radius:.6rem;
          color:#FFFFFF;
          background:rgba(8,12,24,.78);
          font-family:'Inter',sans-serif;
          font-size:.8rem;
        }
        .community-stay-connected__form>input::placeholder{color:#748095}
        .community-stay-connected__form>input:focus-visible{
          border-color:#F9A8D4;
          outline:2px solid rgba(220,60,145,.22);
          outline-offset:1px;
        }
        .community-stay-connected__honeypot{
          position:absolute !important;
          width:1px !important;
          height:1px !important;
          overflow:hidden !important;
          clip:rect(0 0 0 0) !important;
          white-space:nowrap !important;
        }
        .community-stay-connected__cta{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:.42rem;
          min-height:2.45rem;
          padding:.55rem .75rem;
          border:1px solid rgba(220,60,145,.4);
          border-radius:.6rem;
          color:#F9A8D4;
          background:rgba(220,60,145,.06);
          font-family:'Inter',sans-serif;
          font-size:.76rem;
          font-weight:700;
          text-decoration:none;
          transition:background .18s,border-color .18s,color .18s,transform .18s;
          cursor:pointer;
        }
        .community-stay-connected__cta:hover,
        .community-stay-connected__cta:focus-visible{
          border-color:#DC3C91;
          color:#FFFFFF;
          background:rgba(220,60,145,.18);
          outline:0;
          transform:translateY(-1px);
        }
        .community-stay-connected__cta:disabled{
          cursor:wait;
          opacity:.72;
          transform:none;
        }
        .community-stay-connected__spinner{animation:community-newsletter-spin .8s linear infinite}
        .community-stay-connected__form small{
          color:#8793A8;
          font-family:'Inter',sans-serif;
          font-size:.66rem;
          line-height:1.4;
        }
        .community-stay-connected__error{
          margin:0;
          color:#FDA4AF;
          font-family:'Inter',sans-serif;
          font-size:.7rem;
          line-height:1.45;
        }
        .community-stay-connected__success{
          display:flex;
          align-items:center;
          gap:.55rem;
          min-height:3rem;
          padding:.7rem .8rem;
          border:1px solid rgba(74,222,128,.32);
          border-radius:.65rem;
          color:#BBF7D0;
          background:rgba(22,163,74,.1);
          font-family:'Inter',sans-serif;
          font-size:.77rem;
          font-weight:700;
          grid-column:1 / -1;
        }
        @keyframes community-newsletter-spin{to{transform:rotate(360deg)}}
        @media(prefers-reduced-motion:reduce){.community-stay-connected__spinner{animation:none}}
      `}</style>
    </section>
  );
}